import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Aperture, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useOrganization } from '../contexts/OrganizationContext';
import { useUser } from '../contexts/UserContext';
import { usePermissions } from '../hooks/usePermissions';
import { apiService } from '../services/api';
import { AttachableTemplate, Lens } from '../types/api';
import { PageHeader } from '../components/PageHeader';
import { EmptyState } from '../components/EmptyState';
import { ListEmptyState } from '../components/ListEmptyState';
import { ErrorState } from '../components/ErrorState';
import ConfirmDialog from '../components/ConfirmDialog';
import { VisibilityField } from '../components/VisibilityField';
import { sameSet, trimmedText, useRecordChanges, type RecordComparators } from '../hooks/useRecordChanges';
import { useDiscardGuard } from '../hooks/useDiscardGuard';
import {
  DataTable,
  DataTableColumn,
  SearchInput,
  SortState,
  hasListError,
  isListRetrying,
  listTableProps,
} from '../components/DataTable';
import { ColumnPicker } from '../components/ColumnPicker';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { useInfiniteList } from '../hooks/useInfiniteList';
import { useResource } from '../hooks/useResource';
import { InMemoryListConfig, batchFetcher } from '../lib/listPage';
import { Field } from '../components/Field';
import { Button, DisabledReasonText } from '../components/ui/button';
import { DialogActions, DialogForm } from '../components/DialogActions';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { EMPTY_VALUE } from '../lib/formatters';
import { classifyError, humanizeError } from '../lib/errors';
import { FormError } from '../components/FormError';
import { lensCreatedToast, lensUpdatedToast, validateLensDraft, type LensFieldErrors } from '../lib/lensForm';
import { LensBasicsFields } from '../components/LensBasicsFields';
import { LensTemplateSelector } from '../components/LensTemplateSelector';
import { checkboxListProps } from '../components/CheckboxList';
import { lensCol } from '../columns/lensColumns';
import { PropertyList, PropertyRow } from '../components/PropertyList';
import { routeCopy } from '../routes';
import { permissionLabel } from '../lib/permissions';

const { title: PAGE_TITLE, description: PAGE_DESCRIPTION } = routeCopy('/lenses');

/*
 * Re-exported from lib/lensForm rather than declared here: the publish wizard
 * creates lenses too now, and an alias rule that disagrees between the two
 * forms — or with the API's own regex — accepts input the server then refuses.
 */
/** Rung 3 reason, in product language: nothing else on screen says why. */
const PUBLIC_DELETE_BLOCKED_COPY = `Only someone with the "${permissionLabel('lenses:make_public')}" permission can delete a public lens. Ask an organization admin.`;

const TABLE_ID = 'my-lenses';
const DEFAULT_SORT: SortState = { column: 'name', direction: 'asc' };

type LensSortKey = 'name' | 'alias' | 'visibility' | 'templates' | 'createdAt' | 'updatedAt';

const SORTABLE_KEYS = [
  'name',
  'alias',
  'visibility',
  'templates',
  'createdAt',
  'updatedAt',
] as const satisfies readonly LensSortKey[];

/**
 * `GET /lenses` takes only `organizationId` and returns the whole array, so
 * search, sort and paging all happen here. NOTE the fourth search field: the
 * card grid searched a lens by the NAMES OF ITS TEMPLATES as well as its own
 * name/alias/description, and dropping that would be a silent regression.
 */
const LENS_LIST_CONFIG: InMemoryListConfig<Lens, LensSortKey> = {
  searchFields: (lens) => [
    lens.name,
    lens.alias,
    lens.description,
    ...lens.processorTemplates.map((template) => template.name),
  ],
  sortAccessors: {
    name: (lens) => lens.name,
    alias: (lens) => lens.alias,
    visibility: (lens) => lens.isPublic,
    templates: (lens) => lens.processorTemplates.length,
    createdAt: (lens) => Date.parse(lens.createdAt),
    updatedAt: (lens) => Date.parse(lens.updatedAt),
  },
  tiebreak: (lens) => lens.id,
};

type LensFormState = {
  name: string;
  alias: string;
  description: string;
  isPublic: boolean;
  processorTemplateIds: Set<string>;
};

const emptyForm = (): LensFormState => ({
  name: '',
  alias: '',
  description: '',
  isPublic: false,
  processorTemplateIds: new Set<string>(),
});

const LENS_FORM_COMPARATORS: RecordComparators<LensFormState> = {
  name: trimmedText,
  alias: trimmedText,
  description: trimmedText,
  processorTemplateIds: sameSet,
};

const lensToForm = (lens: Lens): LensFormState => ({
  name: lens.name,
  alias: lens.alias,
  description: lens.description ?? '',
  isPublic: lens.isPublic,
  processorTemplateIds: new Set(lens.processorTemplates.map((t) => t.id)),
});

const MyLenses: React.FC = () => {
  const { selectedOrganization, isLoading: orgLoading } = useOrganization();
  const { accessToken } = useUser();
  const { hasPermission } = usePermissions();

  const canWrite = hasPermission('lenses:write');
  const canDelete = hasPermission('lenses:delete');
  const canMakePublic = hasPermission('lenses:make_public');

  const orgId = selectedOrganization?.id ?? null;


  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editLens, setEditLens] = useState<Lens | null>(null);
  const [pendingDeleteLens, setPendingDeleteLens] = useState<Lens | null>(null);

  const [form, setForm] = useState<LensFormState>(emptyForm());
  /** What the dialog opened with. State set on open, never a memo over the row. */
  const [formBaseline, setFormBaseline] = useState<LensFormState | null>(null);
  const [fieldErrors, setFieldErrors] = useState<LensFieldErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  /**
   * The lens list. GET /lenses answers the whole array when asked without list
   * parameters, and several columns (visibility, template count, template
   * names in search) have no server sort, so this is a memory-source list: it
   * reveals 50 rows at a time. Writes pin, patch or remove rows; nothing
   * reloads (ruling 20).
   */
  const lensesQuery = useInfiniteList<Lens, LensSortKey>({
    source: 'memory',
    load: ({ signal }) => apiService.getLenses(orgId as string, accessToken as string, signal),
    memory: LENS_LIST_CONFIG,
    rowKey: (lens) => lens.id,
    enabled: Boolean(orgId && accessToken),
    resetKey: orgId,
    initialSort: DEFAULT_SORT as { column: LensSortKey; direction: 'asc' | 'desc' },
    sortColumns: SORTABLE_KEYS,
    urlState: true,
    errorContext: "Couldn't load lenses",
  });

  const dialogOpen = isCreateOpen || Boolean(editLens);
  /**
   * Opening a lens needs only lenses:read (DESIGN_SYSTEM.md §6.1 Row grammar);
   * the dialog subtracts. A reader sees the lens as values with Close, never a
   * form they cannot submit — and the template picker never loads for them.
   */
  const lensReadOnly = Boolean(editLens) && !canWrite;

  /*
   * The template picker: the attachable set (this organization's own, plus
   * every published one), own first, searched on the server and loaded in
   * batches (ruling 21). Enabled only while the dialog is open, so it is fresh
   * on every open. Search narrows what is DRAWN, never what is selected.
   */
  const templatesList = useInfiniteList<AttachableTemplate>({
    ...batchFetcher<AttachableTemplate>(async ({ limit, offset, cursor, search, signal }) => {
      const result = await apiService.getAttachableTemplatesBatch(orgId as string, accessToken as string, {
        limit,
        offset,
        cursor,
        search: search || undefined,
        signal,
      });
      return { items: result.templates, pagination: result.pagination };
    }),
    rowKey: (template) => template.id,
    enabled: dialogOpen && !lensReadOnly && Boolean(orgId && accessToken),
    resetKey: orgId,
    errorContext: 'Failed to load processor templates',
  });
  const templates = templatesList.items;

  /*
   * Selected templates outside the loaded batches, fetched by id so they show
   * as selected rather than as missing. An id the server does not return is a
   * template this organization can no longer attach (the orphan warning).
   */
  const loadedTemplateIds = useMemo(() => new Set(templates.map((t) => t.id)), [templates]);
  const unloadedSelectedIds = useMemo(
    () => Array.from(form.processorTemplateIds).filter((id) => !loadedTemplateIds.has(id)).sort(),
    [form.processorTemplateIds, loadedTemplateIds],
  );
  const unloadedKey = unloadedSelectedIds.join(',');
  const selectedOutsideResource = useResource(
    async (signal) => {
      const ids = unloadedKey.split(',').filter(Boolean).slice(0, 100);
      const result = await apiService.getAttachableTemplatesBatch(orgId as string, accessToken as string, {
        limit: Math.max(1, ids.length),
        offset: 0,
        ids,
        signal,
      });
      return { ids, templates: result.templates };
    },
    {
      enabled:
        dialogOpen && !lensReadOnly && Boolean(orgId && accessToken && unloadedKey) && templatesList.status !== 'initial',
      deps: [unloadedKey, dialogOpen],
    },
  );
  const selectedOutside = selectedOutsideResource.data?.templates ?? [];

  // Reset modal/form state when active org changes to prevent cross-org bleed.
  // The ROWS are reset by the list hooks' `resetKey`.
  useEffect(() => {
    setIsCreateOpen(false);
    setEditLens(null);
    setPendingDeleteLens(null);
    setForm(emptyForm());
    setFieldErrors({});
    setModalError(null);
  }, [orgId]);

  /**
   * Names for the orphan-template warning in the edit dialog: the org's own
   * templates, plus the edited lens's own references as a fallback for one the
   * org no longer lists. It used to sweep every loaded lens, which is now only
   * the current page — and unnecessary, since the only ids ever resolved here
   * belong to `editLens`.
   */
  const templateNameById = useMemo(() => {
    const map = new Map<string, string>();
    templates.forEach((t) => map.set(t.id, t.name));
    selectedOutside.forEach((t) => map.set(t.id, t.name));
    editLens?.processorTemplates.forEach((t) => {
      if (!map.has(t.id)) map.set(t.id, t.name);
    });
    return map;
  }, [templates, selectedOutside, editLens]);

  /**
   * The picker's rows, split into its two sections by LensTemplateSelector. A
   * public lens may hold only public templates, so while `form.isPublic` is on
   * this organization's private templates are shown disabled with the reason,
   * rather than hidden — hiding them would leave the designer wondering where
   * their own work went.
   */
  // Selected templates outside the loaded batches are pinned first (not while
  // searching, where the server's answer is the whole story).
  const pickerRows = useMemo(
    () => [
      ...(templatesList.search ? [] : selectedOutside.filter((t) => !loadedTemplateIds.has(t.id))),
      ...templates,
    ],
    [templatesList.search, selectedOutside, loadedTemplateIds, templates],
  );

  const openCreate = () => {
    setForm(emptyForm());
    setFormBaseline(emptyForm());
    templatesList.clearSearch();
    setFieldErrors({});
    setModalError(null);
    setIsCreateOpen(true);
  };

  const openEdit = (lens: Lens) => {
    setForm(lensToForm(lens));
    setFormBaseline(lensToForm(lens));
    templatesList.clearSearch();
    setFieldErrors({});
    setModalError(null);
    setEditLens(lens);
  };

  const closeModals = () => {
    setIsCreateOpen(false);
    setEditLens(null);
    setFieldErrors({});
    setModalError(null);
    templatesList.clearSearch();
  };

  const updateFormField = <K extends keyof LensFormState>(key: K, value: LensFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    // A field's error clears as soon as that field changes (DESIGN_SYSTEM §5 Validation).
    if (key === 'name') setFieldErrors((prev) => (prev.name ? { ...prev, name: undefined } : prev));
    if (key === 'alias') setFieldErrors((prev) => (prev.alias ? { ...prev, alias: undefined } : prev));
  };

  const toggleTemplate = (id: string) => {
    setForm((prev) => {
      const next = new Set(prev.processorTemplateIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...prev, processorTemplateIds: next };
    });
  };

  const validateForm = (): boolean => {
    const errors = validateLensDraft(form);
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!orgId || !accessToken) return;
    if (!validateForm()) return;
    setIsSaving(true);
    setModalError(null);
    try {
      const created = await apiService.createLens(
        {
          name: form.name.trim(),
          alias: form.alias.trim(),
          description: form.description.trim() || null,
          isPublic: canMakePublic ? form.isPublic : false,
          processorTemplateIds: Array.from(form.processorTemplateIds),
        },
        orgId,
        accessToken,
      );
      setIsCreateOpen(false);
      toast.success(lensCreatedToast(created.name));
      lensesQuery.prependItem(created, { mark: 'new' });
    } catch (err: unknown) {
      console.error('Failed to create lens:', err);
      reportSaveFailure(err, "Couldn't create the lens");
    } finally {
      setIsSaving(false);
    }
  };

  /** A taken alias belongs under the Alias field; anything else above the footer. */
  const reportSaveFailure = (err: unknown, context: string) => {
    const failure = classifyError(err);
    if (failure.kind === 'conflict' && failure.field === 'alias') {
      setFieldErrors((prev) => ({ ...prev, alias: humanizeError(err) }));
    } else {
      setModalError(humanizeError(err, context));
    }
  };

  const handleUpdate = async () => {
    if (!orgId || !accessToken || !editLens) return;
    if (!validateForm()) return;
    setIsSaving(true);
    setModalError(null);
    try {
      const updated = await apiService.updateLens(
        editLens.id,
        {
          name: form.name.trim(),
          alias: form.alias.trim(),
          description: form.description.trim() || null,
          // Only send isPublic when the caller can change it AND the value
          // actually differs from the current state. This avoids no-op writes
          // and keeps the API's silent-coerce branch a defense-in-depth path.
          ...(canMakePublic && form.isPublic !== editLens.isPublic
            ? { isPublic: form.isPublic }
            : {}),
          processorTemplateIds: Array.from(form.processorTemplateIds),
        },
        orgId,
        accessToken,
      );
      setEditLens(null);
      toast.success(lensUpdatedToast(updated.name));
      lensesQuery.patchItem(updated.id, () => updated, { mark: 'updated' });
    } catch (err: unknown) {
      console.error('Failed to update lens:', err);
      reportSaveFailure(err, "Couldn't update the lens");
    } finally {
      setIsSaving(false);
    }
  };

  /** Throwing surfaces the failure inline in the ConfirmDialog, which stays open. */
  const confirmDelete = async () => {
    if (!orgId || !accessToken || !pendingDeleteLens) return;
    const lens = pendingDeleteLens;
    try {
      await apiService.deleteLens(lens.id, orgId, accessToken);
    } catch (err: unknown) {
      console.error('Failed to delete lens:', err);
      throw err;
    }
    toast.success('Lens deleted');
    // Close edit modal if it was for the same lens.
    if (editLens && editLens.id === lens.id) setEditLens(null);
    lensesQuery.removeItem(lens.id);
  };

  // Known only once the by-id read answered: an id it did not return is gone.
  const orphanTemplateIds = useMemo(() => {
    if (!editLens || selectedOutsideResource.status !== 'success' || !selectedOutsideResource.data) {
      return [] as string[];
    }
    const returned = new Set(selectedOutsideResource.data.templates.map((t) => t.id));
    return selectedOutsideResource.data.ids.filter(
      (id) => form.processorTemplateIds.has(id) && !returned.has(id) && !loadedTemplateIds.has(id),
    );
  }, [editLens, selectedOutsideResource.status, selectedOutsideResource.data, form.processorTemplateIds, loadedTemplateIds]);

  const editDeleteBlocked = Boolean(editLens && editLens.isPublic && !canMakePublic);

  const { isDirty: formDiffers } = useRecordChanges(formBaseline, form, LENS_FORM_COMPARATORS);
  const formDirty = !lensReadOnly && formDiffers;
  // X, Escape, outside click, Cancel and Delete lens all route through one guard.
  const guard = useDiscardGuard({
    isDirty: formDirty,
    isBusy: isSaving,
    onClose: closeModals,
    recordNoun: editLens ? `lens "${editLens.name}"` : 'the new lens',
  });

  const columns: Array<DataTableColumn<Lens>> = [
    { ...lensCol.name<Lens>(), sortable: true, className: 'max-w-[240px] truncate font-medium' },
    {
      key: 'alias',
      header: 'Alias',
      label: 'Alias',
      hideable: true,
      hideBelow: 'md',
      sortable: true,
      className: 'truncate font-mono text-xs text-muted-foreground',
      render: (lens) => lens.alias,
    },
    { ...lensCol.visibility<Lens>(), label: 'Visibility', hideable: true, sortable: true },
    {
      ...lensCol.templates<Lens>((lens) => ({
        names: lens.processorTemplates.map((template) => template.name),
        total: lens.processorTemplates.length,
      })),
      label: 'Templates',
      hideable: true,
      sortable: true,
      className: 'max-w-[260px]',
    },
    {
      key: 'description',
      header: 'Description',
      label: 'Description',
      hideable: true,
      className: 'max-w-[320px] truncate text-muted-foreground',
      render: (lens) => lens.description || EMPTY_VALUE,
    },
    // One date format per table: both are DateCell (date, full time on hover).
    { ...lensCol.updated<Lens>(), label: 'Updated', hideable: true, sortable: true },
    { ...lensCol.created<Lens>(), label: 'Created', hideable: true, defaultHidden: true, sortable: true },
    // No actions column: the row opens through DataTable's Info button, and
    // Delete lens lives in the lens dialog's body.
  ];

  // Destroying the record lives in the body, below the fields it destroys,
  // never in the footer (DESIGN_SYSTEM.md §5). Gated on lenses:delete, so a
  // reader holding it sees it in the values view too.
  const deleteLensSection =
    editLens && canDelete ? (
      <div className="flex flex-wrap items-center justify-start gap-3 border-t pt-4">
        <Button
          type="button"
          variant="destructive"
          onClick={() => guard.confirmDiscard(() => setPendingDeleteLens(editLens))}
          disabled={isSaving}
          disabledReason={editDeleteBlocked ? PUBLIC_DELETE_BLOCKED_COPY : undefined}
        >
          <Trash2 aria-hidden="true" />
          Delete lens
        </Button>
        {editDeleteBlocked ? <DisabledReasonText>{PUBLIC_DELETE_BLOCKED_COPY}</DisabledReasonText> : null}
      </div>
    ) : null;

  const columnVisibility = useColumnVisibility<Lens>({
    tableId: TABLE_ID,
    columns,
    defaultSort: DEFAULT_SORT,
  });

  const sortGuard = {
    activeSort: lensesQuery.sort,
    onSortChange: lensesQuery.onSortChange,
  };

  const emptyState = (
    <ListEmptyState
      icon={Aperture}
      noun="lenses"
      search={lensesQuery.search}
      onClearSearch={lensesQuery.clearSearch}
      firstRun={{
        title: 'No lenses yet',
        description: 'Create a lens, add templates to it, and share its alias with SDK users.',
        action: canWrite ? (
          <Button onClick={openCreate}>
            <Plus aria-hidden="true" />
            Create your first lens
          </Button>
        ) : undefined,
      }}
    />
  );

  const renderContent = () => {
    if (!selectedOrganization && !orgLoading) {
      return (
        <EmptyState
          icon={Aperture}
          title="No organization selected"
          description="Select an organization from the switcher to view its lenses."
        />
      );
    }

    return (
      <DataTable
        {...listTableProps(lensesQuery)}
        listState={orgLoading ? 'initial' : lensesQuery.status}
        columns={columnVisibility.columns}
        rowKey={(lens) => lens.id}
        itemNoun="lenses"
        listLabel="Lenses"
        searchable
        search={
          <SearchInput
            value={lensesQuery.searchInput}
            onChange={lensesQuery.setSearchInput}
            noun="lenses"
            placeholder="Search by name, alias, or template…"
            shortcut
          />
        }
        columnPicker={<ColumnPicker columns={columns} state={columnVisibility} sortGuard={sortGuard} />}
        errorState={
          hasListError(lensesQuery) ? (
            <ErrorState
              title="Couldn't load lenses"
              error={lensesQuery.error}
              onRetry={lensesQuery.reset}
              isRetrying={isListRetrying(lensesQuery)}
              action={lensesQuery.search ? (
                <Button variant="outline" size="sm" onClick={lensesQuery.clearSearch}>Clear search</Button>
              ) : undefined}
            />
          ) : undefined
        }
        emptyState={emptyState}
        // Opening needs read only; the dialog subtracts (lensReadOnly).
        onOpenRow={openEdit}
        openLabel={(lens) => lens.name}
      />
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <div className="shrink-0">
      <PageHeader
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        actions={
          selectedOrganization && canWrite ? (
            <Button onClick={openCreate}>
              <Plus aria-hidden="true" />
              Create lens
            </Button>
          ) : undefined
        }
      />
      </div>

      {renderContent()}

      <Dialog
        open={isCreateOpen || Boolean(editLens)}
        pending={isSaving}
        onOpenChange={guard.onOpenChange}
      >
        <DialogContent size="md">
          <DialogHeader>
            {/* Named from the saved record, never the live form. */}
            <DialogTitle>
              {lensReadOnly && editLens
                ? editLens.name
                : editLens
                  ? `Edit lens "${editLens.name}"`
                  : 'Create lens'}
            </DialogTitle>
            <DialogDescription>
              {lensReadOnly
                ? 'Lens details.'
                : editLens
                  ? 'Update lens details and the set of processor templates it groups.'
                  : 'Group processor templates from this organization into a lens.'}
            </DialogDescription>
          </DialogHeader>

          {lensReadOnly && editLens ? (
            <>
              <DialogBody className="space-y-4">
                <PropertyList>
                  <PropertyRow label="Name">{editLens.name}</PropertyRow>
                  <PropertyRow label="Alias" mono>{editLens.alias}</PropertyRow>
                  <PropertyRow label="Description">
                    {editLens.description ? <span className="whitespace-pre-wrap">{editLens.description}</span> : null}
                  </PropertyRow>
                  <PropertyRow label="Processor templates">
                    {editLens.processorTemplates.length === 0 ? null : (
                      <ul className="space-y-1">
                        {editLens.processorTemplates.map((template) => (
                          <li key={template.id}>{template.name}</li>
                        ))}
                      </ul>
                    )}
                  </PropertyRow>
                </PropertyList>
                <VisibilityField kind="lens" readOnly checked={editLens.isPublic} />
                {deleteLensSection}
              </DialogBody>
              <DialogActions>
                <Button type="button" variant="outline" onClick={closeModals}>
                  Close
                </Button>
              </DialogActions>
            </>
          ) : (
          <DialogForm
            onSubmit={(event) => {
              event.preventDefault();
              void (editLens ? handleUpdate() : handleCreate());
            }}
          >
          <DialogBody className="space-y-4">
            {/* Visibility is changed only with lenses:make_public. Without it an
                existing lens shows its visibility and what that means, and a
                new lens is private (the value is forced false on create). */}
            <LensBasicsFields
              value={form}
              onChange={(key, value) => updateFormField(key, value as LensFormState[typeof key])}
              errors={fieldErrors}
              visibility={canMakePublic ? 'edit' : editLens ? 'readOnly' : 'hidden'}
            />

            <div className="space-y-2">
              <LensTemplateSelector
                options={pickerRows}
                lensIsPublic={form.isPublic}
                selectedIds={form.processorTemplateIds}
                onToggle={toggleTemplate}
                idPrefix="lens-template"
                listProps={checkboxListProps(templatesList)}
                emptyMessage={
                  form.processorTemplateIds.size > 0
                    ? 'No processor templates are available to add. Selections from this lens are kept until you save.'
                    : 'No processor templates are available to add.'
                }
              />
              {/* If the lens currently references templates we can't see in the org list (e.g. just deleted), surface them here so the user knows. */}
              {editLens && orphanTemplateIds.length > 0 && (
                <Alert variant="warning">
                  <AlertTriangle aria-hidden="true" />
                  <div>
                    <AlertTitle>
                      {orphanTemplateIds.length} selected template{orphanTemplateIds.length === 1 ? '' : 's'} no longer available in this organization.
                    </AlertTitle>
                    <AlertDescription>
                      <ul className="list-inside list-disc">
                        {orphanTemplateIds.map((id) => (
                          <li key={id}>{templateNameById.get(id) ?? id}</li>
                        ))}
                      </ul>
                      <p className="mt-1">Saving will remove them from this lens.</p>
                    </AlertDescription>
                  </div>
                </Alert>
              )}
            </div>

            {deleteLensSection}
          </DialogBody>

          <FormError message={modalError} />
          <DialogActions>
            <Button type="button" variant="outline" onClick={guard.requestClose} disabled={isSaving}>
              Cancel
            </Button>
            {/* Edit wakes on a difference; Create lens follows validity. */}
            <Button type="submit" loading={isSaving} disabled={Boolean(editLens) && !formDirty}>
              {editLens ? 'Save changes' : 'Create lens'}
            </Button>
          </DialogActions>
          </DialogForm>
          )}
        </DialogContent>
      </Dialog>

      {guard.guardDialog}

      <ConfirmDialog
        open={Boolean(pendingDeleteLens)}
        title={`Delete lens "${pendingDeleteLens?.name ?? ''}"?`}
        // Verified: subscriptions.lens_id is ON DELETE SET NULL (migration
        // 0018) and the subscription worker's lens gate drops every pair of a
        // subscription whose lens is gone (services/subscriptionMatching.ts).
        // A deleted lens cannot come back, so neither do they. No timing is
        // stated: nothing re-drives the worker at delete time.
        description={
          pendingDeleteLens
            ? `Subscriptions built on this lens stop receiving events and will not resume${
                pendingDeleteLens.isPublic
                  ? ', including subscriptions in other organizations, because this lens is public'
                  : ''
              }. Its processor templates are not affected. This can't be undone.`
            : ''
        }
        confirmLabel="Delete lens"
        variant="destructive"
        typeToConfirm={pendingDeleteLens?.name}
        errorContext="Couldn't delete the lens"
        onConfirm={confirmDelete}
        onClose={() => setPendingDeleteLens(null)}
      />
    </div>
  );
};

export default MyLenses;
